/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";

import Nav from "../components/Nav";

import RoutesModal from "../modals/RoutesModal";

import {
  useLoadingStore,
  useModalStore,
  useStationsStore,
} from "../stores/hooks";

import { getColorsByLine } from "../utils/common";

// 첫 글자의 초성만 추출하는 함수
const getFirstConsonant = (str) => {
  if (!str) return "";

  const consonants = [
    "ㄱ",
    "ㄲ",
    "ㄴ",
    "ㄷ",
    "ㄸ",
    "ㄹ",
    "ㅁ",
    "ㅂ",
    "ㅃ",
    "ㅅ",
    "ㅆ",
    "ㅇ",
    "ㅈ",
    "ㅉ",
    "ㅊ",
    "ㅋ",
    "ㅌ",
    "ㅍ",
    "ㅎ",
  ];

  const firstChar = str[0];
  const code = firstChar.charCodeAt(0) - 0xac00;

  if (code > -1 && code < 11172) {
    // 한글인 경우
    return consonants[Math.floor(code / 588)];
  }
  return firstChar; // 한글이 아닌 경우
};

const Map = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });
  const [startDistance, setStartDistance] = useState(null);
  const [initialPinchScale, setInitialPinchScale] = useState(null);

  const containerRef = useRef(null);
  const contentRef = useRef(null);

  // 초기 중앙 위치 계산
  useEffect(() => {
    if (containerRef.current && contentRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const contentWidth = contentRef.current.clientWidth;
      const containerHeight = containerRef.current.clientHeight;
      const contentHeight = contentRef.current.clientHeight;

      const centerX = -(contentWidth - containerWidth) / 2;
      const centerY = -(contentHeight - containerHeight) / 2;

      setPosition({ x: centerX, y: centerY });
    }
  }, []);

  // 줌 레벨 제한 함수
  const constrainScale = (newScale) => {
    return Math.min(Math.max(newScale, 0.5), 3);
  };

  // 위치 제한 함수
  const constrainPosition = (pos, scale) => {
    const containerWidth = containerRef.current?.clientWidth || 0;
    const contentWidth = (contentRef.current?.clientWidth || 0) * scale;
    const containerHeight = containerRef.current?.clientHeight || 0;
    const contentHeight = (contentRef.current?.clientHeight || 0) * scale;

    const minX = -(contentWidth - containerWidth);
    const maxX = 0;
    const minY = -(contentHeight - containerHeight);
    const maxY = 0;

    return {
      x: Math.min(Math.max(pos.x, minX), maxX),
      y: Math.min(Math.max(pos.y, minY), maxY),
    };
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setStartPos({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
    document.body.style.cursor = "grabbing";
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );
      setStartDistance(dist);
      setInitialPinchScale(scale);
    } else if (e.touches.length === 1) {
      setIsDragging(true);
      setStartPos({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;

    const newPosition = {
      x: e.clientX - startPos.x,
      y: e.clientY - startPos.y,
    };

    setPosition(constrainPosition(newPosition, scale));
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY,
      );

      if (startDistance && initialPinchScale) {
        const delta = dist / startDistance;
        const newScale = constrainScale(initialPinchScale * delta);
        setScale(newScale);
      }
    } else if (isDragging && e.touches.length === 1) {
      const newPosition = {
        x: e.touches[0].clientX - startPos.x,
        y: e.touches[0].clientY - startPos.y,
      };

      setPosition(constrainPosition(newPosition, scale));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = "default";
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setStartDistance(null);
    setInitialPinchScale(null);
  };

  const handleZoomIn = () => {
    const newScale = constrainScale(scale + 0.1);
    setScale(newScale);
    setPosition(constrainPosition(position, newScale));
  };

  const handleZoomOut = () => {
    const newScale = constrainScale(scale - 0.1);
    setScale(newScale);
    setPosition(constrainPosition(position, newScale));
  };

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, startPos, scale, startDistance, initialPinchScale]);

  return (
    <div
      ref={containerRef}
      className="fixed w-full max-w-[736px] mx-auto h-[calc(100vh-56px-120px)] bottom-0 left-0 right-0 overflow-hidden"
    >
      <div
        ref={contentRef}
        className="absolute cursor-grab active:cursor-grabbing"
        style={{
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transformOrigin: "0 0",
          touchAction: "none",
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="w-[2000px] h-[2000px]">
          <img src="/assets/images/seoul-metro.jpeg" alt="서울 지하철 노선도" />
        </div>
      </div>

      {/* 줌 컨트롤 버튼 */}
      <div className="absolute bottom-15 right-15 flex flex-col z-10 bg-white border-1 border-gray-8 rounded-12 shadow-[2px_2px_20px_0px_rgba(0,0,0,0.10)]">
        <button onClick={handleZoomIn} className="p-8">
          <img src="/assets/images/icon-plus.svg" alt="확대" className="w-24" />
        </button>
        <div className="px-4">
          <div className="w-full h-1 bg-gray-9" />
        </div>
        <button onClick={handleZoomOut} className="p-8">
          <img
            src="/assets/images/icon-minus.svg"
            alt="축소"
            className="w-24"
          />
        </button>
      </div>
    </div>
  );
};

const SearchStationWrap = ({
  type,
  focusedPosition,
  selectedStation,
  onChangeFocusedPosition,
  onSelect,
  focusing,
  onAfterEnter,
}) => {
  const { state } = useStationsStore();

  const [searchText, setSearchText] = useState("");
  const [isFocusedForClear, setIsFocusedForClear] = useState(false);
  const [hoverStation, setHoverStation] = useState(null);
  const isFocused = focusedPosition === type;

  const inputRef = useRef(null);

  useEffect(() => {
    if (focusing && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 100);
    }
  }, [focusing, inputRef]);

  useEffect(() => {
    if (!isFocusedForClear) {
      setSearchText(selectedStation?.name || "");
    }
  }, [isFocusedForClear, selectedStation]);

  useEffect(() => {
    setSearchText(selectedStation?.name || "");
  }, [selectedStation]);

  const handleChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleClear = () => {
    setSearchText("");
    onSelect(null);
    inputRef.current.focus();
  };

  const handleFocus = () => {
    setTimeout(() => {
      setIsFocusedForClear(true);
      onChangeFocusedPosition(type);
    }, 200);
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsFocusedForClear(false);
      onChangeFocusedPosition(null);
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !!searchText && !!filteredStations.length) {
      setTimeout(() => {
        setSearchText(filteredStations[0].name);
        onSelect(filteredStations[0]);
        onAfterEnter();
        inputRef.current.blur();
      }, 100);
    }
  };

  const handleKeyDownList = (e, station) => {
    if (e.key === "Enter") {
      setSearchText(station.name);
      onSelect(station);
      onAfterEnter();

      e.preventDefault();
      e.stopPropagation();
      e.target.blur();
    }
  };

  const handleMouseOver = (station) => {
    setHoverStation(station);
  };

  const handleMouseOut = () => {
    setHoverStation(null);
  };

  const handleSelectStation = (e, station) => {
    setSearchText(station.name);
    onSelect(station);

    e.preventDefault();
    e.stopPropagation();
    e.target.blur();
  };

  // 검색어와 비교
  const filteredStations = state?.stations?.filter((station) => {
    const stationFirstConsonant = getFirstConsonant(station.name);

    // 검색어가 자음으로 시작하는 경우
    if (searchText.match(/^[ㄱ-ㅎ]/) && searchText.length === 1) {
      return stationFirstConsonant === searchText[0];
    }

    // 일반 검색
    return station.name.includes(searchText);
  });

  return (
    <div
      className={`relative ${
        isFocused ? "outline outline-2 outline-primary-2 rounded-16" : ""
      }`}
    >
      <div className="py-9 px-12 flex justify-start items-center">
        <div className="w-32 h-32 flex justify-center items-center">
          {type === "start" ? (
            <img src="/assets/images/icon-start.svg" alt="" />
          ) : (
            <img src="/assets/images/icon-end.svg" alt="" />
          )}
        </div>
        <div className="flex-1 flex justify-start items-center">
          <input
            ref={inputRef}
            name="stationName"
            placeholder={`${
              type === "start" ? "출발역" : "도착역"
            }을 입력해주세요`}
            className="h-36 text-12 leading-[20px] tracking-[0.03px] px-8 flex-1 w-0 focus-visible:outline-none"
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onChange={handleChange}
            value={searchText}
          />
          {!isFocusedForClear && selectedStation && (
            <div className="flex justify-start items-center gap-2">
              {selectedStation.lines?.map((e) => (
                <div
                  key={e}
                  className={`h-24 min-w-24 py-2 px-8 rounded-12 text-10 font-bold tracking-[0.05px] flex justify-center items-center`}
                  style={getColorsByLine(e, state.lines).style}
                >
                  {e.replace(/^0+/, "").replace(/(호선|선)$/, "")}
                </div>
              ))}
            </div>
          )}
          {isFocusedForClear && (
            <button
              type="button"
              onClick={handleClear}
              className="w-18 ml-8"
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              <img
                src="/assets/images/icon-close-g.svg"
                alt="텍스트 지우기"
                className="w-18"
              />
            </button>
          )}
        </div>
      </div>

      {isFocusedForClear && (
        <div className="w-full h-1 relative px-12">
          <div className="w-full h-1 bg-gray-9" />
        </div>
      )}

      {isFocusedForClear && !!searchText && !!filteredStations.length && (
        <div onMouseOut={handleMouseOut} className="max-h-200 overflow-auto">
          {filteredStations.map((station, idx) => (
            <div
              key={station.id}
              className={`py-8 px-12 h-50 flex justify-between items-center text-neutral-10 text-12 font-bold tracking-[0.06px] cursor-pointer ${
                hoverStation?.id === station.id || (idx === 0 && !hoverStation)
                  ? "bg-primary-6"
                  : ""
              }`}
              onMouseOver={() => handleMouseOver(station)}
              onClick={(e) => handleSelectStation(e, station)}
              onKeyDown={(e) => handleKeyDownList(e, station)}
              tabIndex={0}
              onFocus={handleFocus}
              onBlur={handleBlur}
            >
              <div>{station.name}</div>
              <div className="flex justify-start items-center gap-2">
                {station.lines.map((line) => (
                  <div
                    key={line}
                    className={`h-24 min-w-24 py-2 px-8 rounded-12 text-10 font-bold tracking-[0.05px] flex justify-center items-center`}
                    style={getColorsByLine(line, state.lines).style}
                  >
                    {line.replace(/^0+/, "").replace(/(호선|선)$/, "")}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {isFocusedForClear && !searchText && (
        <div className="text-gray-5 flex justify-center items-center text-12 leading-[20px] tracking-[0.03px] py-8 px-12 h-50">
          무의 수도권 지하철 교통약자 환승지도 경로 검색
        </div>
      )}

      {isFocusedForClear && searchText && !filteredStations.length && (
        <div className="text-primary-1 flex justify-center items-center text-12 leading-[20px] tracking-[0.03px] py-8 px-12 h-50">
          찾으시는 역이 없습니다. 다시 검색해주세요.
        </div>
      )}
    </div>
  );
};

const Search = () => {
  const { ...loadingActions } = useLoadingStore();
  const { modals } = useModalStore().state;
  const { openModal } = useModalStore();
  const { state, ...actions } = useStationsStore();

  const { handleSubmit, setValue, watch, getValues, reset } = useForm();

  const [focusedPosition, setFoucsedPosition] = useState(null);

  const [startFocusing, setStartFocusing] = useState(false);
  const [endFocusing, setEndFocusing] = useState(false);

  useEffect(() => {
    fetchStations();
    fetchLines();
  }, []);

  useEffect(() => {
    if (!modals.length && state.routes) {
      openModal(<RoutesModal key="routes-modal" routes={state.routes} />);
    }
  }, [state.routes, modals]);

  const fetchStations = async () => {
    try {
      const response = await actions.fetchStations();

      if (response.status !== "Success") {
        window.alert(`지하철 목록 조회에 실패했습니다. 다시 시도해주세요.`);
      }
    } catch (e) {
      window.alert(
        `지하철 목록 조회에 실패했습니다. 다시 시도해주세요. ${e.message}`,
      );
    }
  };

  const fetchLines = async () => {
    try {
      const response = await actions.fetchLines();

      if (response.status !== "Success") {
        window.alert(`노선 목록 조회에 실패했습니다. 다시 시도해주세요.`);
      }
    } catch (e) {
      window.alert(
        `노선 목록 조회에 실패했습니다. 다시 시도해주세요. ${e.message}`,
      );
    }
  };

  const handleSelectStartStation = (option) => {
    setValue("startStation", option);
  };

  const handleSelectDpStation = (option) => {
    setValue("dpStation", option);
  };

  const handleChangeFocusedPosition = (_focusedPosition) => {
    setFoucsedPosition(_focusedPosition);
  };

  const handleAfterEnter = () => {
    if (watch("startStation") && !watch("dpStation")) {
      setEndFocusing(true);
    } else if (!watch("startStation") && watch("dpStation")) {
      setStartFocusing(true);
    } else if (watch("startStation") && watch("dpStation")) {
      // 검색처리
    }
  };

  const handleExchangeStation = () => {
    reset({
      startStation: getValues().dpStation,
      dpStation: getValues().startStation,
    });
  };

  const handleClearAll = () => {
    reset({
      startStation: null,
      dpStation: null,
    });
  };

  const onSubmit = async (data) => {
    if (data.startStation && data.dpStation) {
      try {
        loadingActions.startLoading();
        const _data = {
          start_station_name: data.startStation.name,
          start_station_line: data.startStation.line,
          dp_station_name: data.dpStation.name,
          dp_station_line: data.dpStation.line,
        };

        const response = await actions.fetchRoutes(_data);

        if (response.status !== "Success") {
          window.alert(`경로 조회에 실패했습니다. 다시 시도해주세요.`);
        }
      } catch (e) {
        window.alert(`경로 조회에 실패했습니다. ${e.message}`);
      }

      loadingActions.finishLoading();
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="px-16 py-16 bg-primary-6 border-b-1 border-gray-8 relative z-[11]">
          <div className="max-w-[420px] mx-auto">
            <div className="bg-white border-1 border-gray-9 rounded-16">
              <SearchStationWrap
                type="start"
                onChangeFocusedPosition={handleChangeFocusedPosition}
                focusedPosition={focusedPosition}
                selectedStation={watch("startStation")}
                onSelect={handleSelectStartStation}
                focusing={startFocusing}
                onAfterEnter={handleAfterEnter}
              />
              {focusedPosition !== "start" && (
                <div className="w-full h-1 relative px-12">
                  <div className="w-full h-1 bg-gray-9"></div>
                  <div className="w-1 h-45 absolute bg-primary-1 left-[27.5px] top-[-22px]" />
                  <button
                    type="button"
                    className="p-6 bg-white border-1 border-gray-8 rounded-12 absolute left-12 top-[-16px] z-[10] hover:bg-gray-8 active:bg-gray-8"
                    onClick={handleExchangeStation}
                  >
                    <img
                      src="/assets/images/icon-switch.svg"
                      alt="출발역과 도착역 변경"
                      className="w-18"
                    />
                  </button>
                </div>
              )}
              <div className={`${focusedPosition === "start" ? "hidden" : ""}`}>
                <SearchStationWrap
                  type="end"
                  onChangeFocusedPosition={handleChangeFocusedPosition}
                  focusedPosition={focusedPosition}
                  selectedStation={watch("dpStation")}
                  onSelect={handleSelectDpStation}
                  focusing={endFocusing}
                  onAfterEnter={handleAfterEnter}
                />
              </div>
            </div>
            {!focusedPosition &&
              (watch("startStation") || watch("dpStation")) && (
                <div className="mt-12 flex justify-end items-center gap-8">
                  <button
                    type="button"
                    onClick={handleClearAll}
                    className="py-8 px-10 text-gray-2 text-center text-12 font-bold leading-[16px] tracking-[0.06px] rounded-12 hover:bg-gray-8 active:bg-gray-8"
                  >
                    다시 입력
                  </button>

                  <button
                    type="submit"
                    disabled={!watch("startStation") || !watch("dpStation")}
                    className={`py-8 px-10 text-gray-2 text-center text-12 font-bold leading-[16px] tracking-[0.06px] rounded-12 bg-gray-8 disabled:text-gray-6`}
                  >
                    경로 검색
                  </button>
                </div>
              )}
          </div>
        </div>
      </form>
      {!!focusedPosition && (
        <div className="bg-[rgba(35,35,52,0.50)] fixed top-56 bottom-0 left-0 right-0 w-full z-[10] max-w-[736px] mx-auto"></div>
      )}
    </>
  );
};

function Main() {
  return (
    <>
      <Nav />
      <div className="pt-40">
        <Search />
        <Map />
      </div>
    </>
  );
}

export default Main;
